import { ReportItem } from '@/constants/ReportDashboard/report-items'
import { IGfpaSecurityContrib } from '@/interfaces/gfpaReport.interface'
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { useAttributionSecurityContribution } from '../FAAttributionDataContext/hooks'

// Ranking metric shared by the attribution Top/Bottom table and the return-contribution bubble chart:
// both rank the same securities so a change in one reflects in the other.
export type AttributionMetric = 'contribution' | 'securityReturn'
export type ShowContributors = 'topBottom' | 'topOnly' | 'bottomOnly'
export type ContributorCount = 5 | 10 | 15

export const METRIC_FIELD: Record<AttributionMetric, keyof IGfpaSecurityContrib> = {
  contribution: 'security_ret_contrib',
  securityReturn: 'security_ret',
}
export const METRIC_LABELS: Record<AttributionMetric, string> = {
  contribution: 'Contribution to Return',
  securityReturn: 'Security Return',
}

export interface AttributionTopBottom {
  topItems: IGfpaSecurityContrib[]
  bottomItems: IGfpaSecurityContrib[]
}

interface AttributionHoldingsContextValue {
  metric: AttributionMetric
  setMetric: (m: AttributionMetric) => void
  showContributors: ShowContributors
  setShowContributors: (v: ShowContributors) => void
  count: ContributorCount
  setCount: (n: ContributorCount) => void
  // Securities restricted to a group selected in the holdings table (null = no filter). The Top/Bottom
  // card and the bubble chart rank/plot over this set so a group selection drives all three cards.
  securitiesFiltered: boolean
  highlightedSecurities: IGfpaSecurityContrib[]
  highlightGroups: string[] | null
  highlightSecurities: (ids: string[]) => void
  setHighlightGroups: (...groups: string[]) => void
  clearHighlightedSecurities: () => void
}

const EMPTY_SECURITIES: IGfpaSecurityContrib[] = []

const AttributionHoldingsContext = createContext<AttributionHoldingsContextValue>({
  metric: 'contribution',
  setMetric: () => {},
  showContributors: 'topBottom',
  setShowContributors: () => {},
  count: 5,
  setCount: () => {},
  securitiesFiltered: false,
  highlightedSecurities: EMPTY_SECURITIES,
  highlightGroups: null,
  highlightSecurities: () => {},
  setHighlightGroups: () => {},
  clearHighlightedSecurities: () => {},
})

export const AttributionHoldingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const rows = useAttributionSecurityContribution()
  const [metric, setMetric] = useState<AttributionMetric>('contribution')
  const [showContributors, setShowContributors] = useState<ShowContributors>('topBottom')
  const [count, setCount] = useState<ContributorCount>(5)
  const [highlightedIds, setHighlightedIds] = useState<string[] | null>(null)
  const [highlightGroups, setHighlightGroups] = useState<string[] | null>(null)
  const securitiesFiltered = highlightedIds != null

  const highlightedSecurities = useMemo((): IGfpaSecurityContrib[] => {
    if (rows == null) return EMPTY_SECURITIES
    if (highlightedIds == null) return rows
    const idSet = new Set(highlightedIds)
    return rows.filter((r) => {
      const id = r[ReportItem.StandardID]
      return id != null && idSet.has(id)
    })
  }, [rows, highlightedIds])

  const clearHighlightedSecurities = useCallback(() => {
    setHighlightedIds(null)
    setHighlightGroups(null)
  }, [])

  return (
    <AttributionHoldingsContext.Provider
      value={{
        metric,
        setMetric,
        showContributors,
        setShowContributors,
        count,
        setCount,
        securitiesFiltered,
        highlightedSecurities,
        highlightGroups,
        highlightSecurities: setHighlightedIds,
        setHighlightGroups: (...groups) => setHighlightGroups(groups),
        clearHighlightedSecurities,
      }}
    >
      {children}
    </AttributionHoldingsContext.Provider>
  )
}

export const useAttributionHoldingsControls = () => useContext(AttributionHoldingsContext)

export const useAttributionHighlightedSecurities = () => useAttributionHoldingsControls().highlightedSecurities

export const useAttributionHighlightedGroups = () => useAttributionHoldingsControls().highlightGroups

// Rank securities by the active metric: top = highest, bottom = lowest (shown lowest-first). At <= count
// securities the sections would overlap, so route them all into the visible one (bottom still
// lowest-first). Between count and 2*count, the bottom slice starts at len-count so they don't intersect.
// Ranks over the highlighted set, so a group selection narrows the Top/Bottom to that group.
export const useAttributionTopBottom = (): AttributionTopBottom => {
  const { metric, count, showContributors, highlightedSecurities } = useAttributionHoldingsControls()
  return useMemo(() => {
    const field = METRIC_FIELD[metric]
    const ranked = highlightedSecurities.filter((r) => r[field] != null).sort((a, b) => Number(b[field]) - Number(a[field]))
    if (ranked.length <= count) {
      const bottomOnly = showContributors === 'bottomOnly'
      return { topItems: bottomOnly ? [] : ranked, bottomItems: bottomOnly ? [...ranked].reverse() : [] }
    }
    return { topItems: ranked.slice(0, count), bottomItems: ranked.slice(Math.max(count, ranked.length - count)).reverse() }
  }, [highlightedSecurities, metric, count, showContributors])
}
